import { useEffect, useState, useMemo } from "react";
import { Receipt, Trash2, Edit2, MoreVertical, DollarSign } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ExpenseEditDialog } from "./History/ExpenseEditDialog";
import { IncomeEditDialog } from "./History/IncomeEditDialog";
import { SyncStatus } from "./History/SyncStatus";
import { MonthSummary } from "./History/MonthSummary";
import { motion } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { trackEvent } from "@/lib/analytics";
import { convertCurrency, formatCurrency } from "@/lib/currencyConversion";
import { useLanguage } from "@/contexts/LanguageContext";
import { useGamification } from "@/contexts/GamificationContext";
import { ExpenseHistorySkeleton } from "./Loading/ExpenseHistorySkeleton";

interface Expense {
  id: string;
  amount: number;
  merchant: string;
  category: string;
  expense_date: string;
  note: string | null;
  currency: string;
  tag?: string | null;
}

interface Income {
  id: string;
  amount: number;
  source: string;
  category: string | null;
  income_date: string;
  note: string | null;
  currency: string;
}

interface TransactionItem {
  id: string;
  type: 'expense' | 'income';
  amount: number;
  label: string;
  category: string;
  date: string;
  note: string | null;
  currency: string;
  tag?: string | null;
  originalData: Expense | Income;
}

interface ExpenseHistoryProps {
  refreshTrigger: number;
  onExpenseDeleted: () => void;
  pushUndo?: (action: any) => void;
  selectedMonth?: Date;
}

const CATEGORY_COLORS = {
  Housing: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  Food: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  Transport: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  Fun: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  Shopping: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  Health: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  Other: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300',
  'Bills/Other': 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300',
};

const CATEGORY_ACCENT = {
  Housing: 'border-l-purple-500',
  Food: 'border-l-green-500',
  Transport: 'border-l-blue-500',
  Fun: 'border-l-pink-500',
  Shopping: 'border-l-orange-500',
  Health: 'border-l-red-500',
  Other: 'border-l-gray-500',
  'Bills/Other': 'border-l-gray-500',
};

export const ExpenseHistory = ({ 
  refreshTrigger, 
  onExpenseDeleted, 
  pushUndo,
  selectedMonth 
}: ExpenseHistoryProps) => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [incomeItems, setIncomeItems] = useState<Income[]>([]);
  const [previousMonthTotal, setPreviousMonthTotal] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [deletingExpense, setDeletingExpense] = useState<Expense | null>(null);
  const [editingIncome, setEditingIncome] = useState<Income | null>(null);
  const [deletingIncome, setDeletingIncome] = useState<Income | null>(null);
  const [isOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [userCurrency, setUserCurrency] = useState('EUR');
  const { toast } = useToast();
  const { t } = useLanguage();
  const { settings } = useGamification();
  const calmMode = settings?.hideRewards ?? false;

  // Use current month if no selectedMonth provided
  const currentMonth = useMemo(() => {
    return selectedMonth || new Date();
  }, [selectedMonth]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const currency = await loadUserCurrency();
      if (cancelled) return;
      await loadExpenses(currency);
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshTrigger, currentMonth]);

  const loadUserCurrency = async (): Promise<string | undefined> => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return undefined;

      const { data } = await supabase
        .from("budget_settings")
        .select("currency")
        .eq("user_id", user.id)
        .maybeSingle();

      const currency = data?.currency || undefined;
      if (currency) setUserCurrency(currency);
      return currency;
    } catch (error) {
      console.error("Error loading user currency:", error);
      return undefined;
    }
  };

  const loadExpenses = async (currencyOverride?: string) => {
    const currencyToUse = currencyOverride || userCurrency;

    setIsSyncing(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      // Calculate month boundaries
      const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

      const startStr = monthStart.toISOString().split("T")[0];
      const endStr = monthEnd.toISOString().split("T")[0];

      // Fetch expenses for selected month
      const { data, error } = await supabase
        .from("expenses")
        .select("*")
        .eq("user_id", user.id)
        .gte("expense_date", startStr)
        .lte("expense_date", endStr)
        .order("expense_date", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw error;
      setExpenses(data || []);

      // Fetch income for selected month
      const { data: incomeData, error: incomeError } = await supabase
        .from("income")
        .select("*")
        .eq("user_id", user.id)
        .gte("income_date", startStr)
        .lte("income_date", endStr)
        .order("income_date", { ascending: false });

      if (!incomeError) {
        setIncomeItems(incomeData || []);
      }

      // Fetch previous month total for comparison (converted to user's currency)
      const prevMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
      const prevMonthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 0);

      const { data: prevData } = await supabase
        .from("expenses")
        .select("amount,currency")
        .eq("user_id", user.id)
        .gte("expense_date", prevMonthStart.toISOString().split("T")[0])
        .lte("expense_date", prevMonthEnd.toISOString().split("T")[0]);

      if (prevData && prevData.length > 0) {
        const prevTotal = prevData.reduce((sum, e) => {
          return sum + convertCurrency(e.amount, e.currency, currencyToUse);
        }, 0);

        setPreviousMonthTotal(prevTotal);
      } else {
        setPreviousMonthTotal(undefined);
      }
    } catch (error) {
      console.error("Load expenses error:", error);
    } finally {
      setIsLoading(false);
      setIsSyncing(false);
    }
  };

  // Listen for scroll-to-income events
  useEffect(() => {
    const handleScrollToIncome = (event: CustomEvent<{ incomeId: string }>) => {
      const { incomeId } = event.detail;
      const element = document.getElementById(`income-${incomeId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedId(incomeId);
        setTimeout(() => setHighlightedId(null), 2000);
      }
    };

    window.addEventListener('scroll-to-income', handleScrollToIncome as EventListener);
    return () => window.removeEventListener('scroll-to-income', handleScrollToIncome as EventListener);
  }, []);

  // Combine expenses and income into transactions
  const allTransactions: TransactionItem[] = useMemo(() => {
    const expenseItems: TransactionItem[] = expenses.map(e => ({
      id: e.id,
      type: 'expense' as const,
      amount: e.amount,
      label: e.merchant,
      category: e.category,
      date: e.expense_date,
      note: e.note,
      currency: e.currency,
      tag: e.tag,
      originalData: e
    }));

    const incomeTransactions: TransactionItem[] = incomeItems.map(i => ({
      id: i.id,
      type: 'income' as const,
      amount: i.amount,
      label: i.source,
      category: i.category || 'Income',
      date: i.income_date,
      note: i.note,
      currency: i.currency,
      originalData: i
    }));

    return [...expenseItems, ...incomeTransactions].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [expenses, incomeItems]);

  // Group transactions by date with "Today" label
  const groupedTransactions = allTransactions.reduce((groups, transaction) => {
    const date = new Date(transaction.date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    let dateKey: string;
    if (date.toDateString() === today.toDateString()) {
      dateKey = t('history.today');
    } else if (date.toDateString() === yesterday.toDateString()) {
      dateKey = t('history.yesterday');
    } else {
      dateKey = date.toLocaleDateString('en-GB', { 
        day: 'numeric', 
        month: 'long',
        year: 'numeric'
      });
    }
    
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(transaction);
    return groups;
  }, {} as Record<string, TransactionItem[]>);

  const handleDelete = async (expense: Expense) => {
    try {
      const { error } = await supabase
        .from('expenses')
        .delete()
        .eq('id', expense.id);

      if (error) throw error;

      trackEvent({ name: 'delete_confirmed' });

      if (pushUndo) {
        pushUndo({ type: 'delete', expense });
      }

      if (!calmMode) {
        toast({
          title: t('history.gotIt'),
          description: t('history.removed', { merchant: expense.merchant }),
          duration: 3000,
        });
      }

      onExpenseDeleted();
      setDeletingExpense(null);
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: t('history.deleteFailed'),
        description: t('history.couldNotDelete'),
        variant: "destructive",
      });
    }
  };

  const handleDeleteIncome = async (income: Income) => {
    try {
      const { error } = await supabase
        .from('income')
        .delete()
        .eq('id', income.id);

      if (error) throw error;

      trackEvent({ name: 'delete_confirmed' });

      toast({
        title: t('history.gotIt'),
        description: t('history.removed', { merchant: income.source }),
        duration: 3000,
      });

      onExpenseDeleted();
      setDeletingIncome(null);
    } catch (error) {
      console.error('Delete income error:', error);
      toast({
        title: t('history.deleteFailed'),
        description: t('history.couldNotDelete'),
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <ExpenseHistorySkeleton />;
  }

  return (
    <>
      <ExpenseEditDialog
        expense={editingExpense}
        open={!!editingExpense}
        onOpenChange={(open) => {
          if (!open) setEditingExpense(null);
        }}
        onUpdate={() => {
          loadExpenses();
          onExpenseDeleted();
        }}
      />

      <IncomeEditDialog
        income={editingIncome}
        open={!!editingIncome}
        onOpenChange={(open) => {
          if (!open) setEditingIncome(null);
        }}
        onUpdate={() => {
          loadExpenses();
          onExpenseDeleted();
        }}
      />

      <AlertDialog open={!!deletingExpense} onOpenChange={(open) => !open && setDeletingExpense(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('history.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              {t('history.deleteMessage', { merchant: deletingExpense?.merchant, amount: formatCurrency(deletingExpense?.amount || 0, deletingExpense?.currency || userCurrency) })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-[44px]">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingExpense && handleDelete(deletingExpense)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 min-h-[44px]"
            >
              {t('history.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deletingIncome} onOpenChange={(open) => !open && setDeletingIncome(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('history.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              {t('history.deleteMessage', { merchant: deletingIncome?.source, amount: formatCurrency(deletingIncome?.amount || 0, deletingIncome?.currency || userCurrency) })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-[44px]">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingIncome && handleDeleteIncome(deletingIncome)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 min-h-[44px]"
            >
              {t('history.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-heading-section">{t('history.title')}</h3>
          <SyncStatus isSyncing={isSyncing} isOnline={isOnline} />
        </div>

        {/* Month Summary Card */}
        <MonthSummary 
          expenses={expenses}
          selectedMonth={currentMonth}
          userCurrency={userCurrency}
          previousMonthTotal={previousMonthTotal}
        />

        {/* Empty state for no transactions */}
        {allTransactions.length === 0 && (
          <div className="text-center py-8 space-y-3">
            <Receipt className="w-12 h-12 mx-auto text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">{t('history.noExpenses')}</p>
          </div>
        )}
        
        {/* Transaction list grouped by date */}
        {Object.entries(groupedTransactions).map(([dateKey, dateTransactions], groupIndex) => (
          <div key={dateKey} className="space-y-3">
            <div className="sticky top-[60px] bg-background/95 backdrop-blur py-2 z-[5] -mx-6 px-6">
              <h4 className="text-date-group">{dateKey}</h4>
            </div>
            <div className="space-y-3">
              {dateTransactions.map((transaction, index) => (
                <motion.div
                  key={transaction.id}
                  id={transaction.type === 'income' ? `income-${transaction.id}` : undefined}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (groupIndex * 0.05) + (index * 0.03), duration: 0.2 }}
                  className={`flex items-center justify-between p-5 bg-card border border-l-4 ${
                    transaction.type === 'income' 
                      ? 'border-l-primary' 
                      : CATEGORY_ACCENT[transaction.category as keyof typeof CATEGORY_ACCENT] || 'border-l-gray-500'
                  } rounded-xl hover:shadow-md transition-all group ${
                    highlightedId === transaction.id ? 'ring-2 ring-primary ring-offset-2 animate-pulse' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0 mr-4">
                    <div className="flex items-center gap-2 mb-1">
                      {transaction.type === 'income' && (
                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                          <DollarSign className="w-3 h-3 text-primary" />
                        </div>
                      )}
                      <p className="font-semibold truncate text-lg leading-tight">
                        {transaction.label}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2.5 py-1 rounded-md text-badge ${
                        transaction.type === 'income'
                          ? 'bg-primary/10 text-primary'
                          : CATEGORY_COLORS[transaction.category as keyof typeof CATEGORY_COLORS] || 'bg-gray-100 text-gray-700'
                      }`}>
                        {transaction.type === 'income' ? 'Income' : transaction.category}
                      </span>
                      {transaction.tag && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-accent/50 text-accent-foreground">
                          {transaction.tag}
                        </span>
                      )}
                      {transaction.note && (
                        <p className="text-sm text-muted-foreground truncate">{transaction.note}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="text-right mr-2">
                      <p className={`text-xl font-bold tabular-nums ${transaction.type === 'income' ? 'text-primary' : ''}`}>
                        {transaction.type === 'income' ? '+' : ''}{formatCurrency(transaction.amount, transaction.currency)}
                      </p>
                      {transaction.currency !== userCurrency && (
                        <p className="text-xs text-muted-foreground">
                          ≈ {formatCurrency(convertCurrency(transaction.amount, transaction.currency, userCurrency), userCurrency)}
                        </p>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="min-h-[44px] min-w-[44px] hover:bg-accent"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-32">
                        <DropdownMenuItem
                          onClick={() => {
                            if (transaction.type === 'expense') {
                              setEditingExpense(transaction.originalData as Expense);
                            } else {
                              setEditingIncome(transaction.originalData as Income);
                            }
                            trackEvent({ name: 'edit_open' });
                          }}
                          className="min-h-[44px]"
                        >
                          <Edit2 className="h-4 w-4 mr-2" />
                          {t('common.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            if (transaction.type === 'expense') {
                              setDeletingExpense(transaction.originalData as Expense);
                            } else {
                              setDeletingIncome(transaction.originalData as Income);
                            }
                          }}
                          className="text-destructive focus:text-destructive min-h-[44px]"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('common.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
